import type { FieldDefinition } from "@noa/api-core";
import { DetailRow } from "./detail-row";
import { Paragraph, Tag, Text } from "~/components/ui";

interface FieldDetailProps {
  field: FieldDefinition;
  t: (key: string) => string;
}

export function FieldDetail({ field, t }: FieldDetailProps) {
  const extraction = field.extraction;
  const vectorstore = field.vectorstore;

  return (
    <div className="space-y-2 text-sm">
      {field.description && (
        <Paragraph tone="muted" className="mb-2 text-sm">
          {field.description}
        </Paragraph>
      )}

      <div className="flex flex-wrap gap-1">
        {field.type && <Tag>{field.type}</Tag>}
        {field.required && <Tag color="red">{t("schemaAdmin.field.required")}</Tag>}
        {field.validation_phase && <Tag color="purple">{field.validation_phase}</Tag>}
        {vectorstore?.filterable && <Tag color="blue">{t("schemaAdmin.field.filterable")}</Tag>}
        {vectorstore?.searchable && <Tag color="green">{t("schemaAdmin.field.searchable")}</Tag>}
      </div>

      {field.allowed_values && field.allowed_values.length > 0 && (
        <div>
          <Text tone="muted">{t("schemaAdmin.field.allowedValues")}: </Text>
          {field.allowed_values.map((v, i) => (
            <Tag key={i} className="text-xs">
              {v}
            </Tag>
          ))}
        </div>
      )}

      {extraction && Object.keys(extraction).length > 0 && (
        <div className="mt-2 p-2 bg-noa-elevated rounded border border-noa text-xs space-y-1">
          {extraction.from_metadata_key && <DetailRow label="metadata_key" code={extraction.from_metadata_key} />}
          {extraction.from_path_pattern && <DetailRow label="path_pattern" code={extraction.from_path_pattern} />}
          {extraction.from_filename_pattern && (
            <DetailRow label="filename_pattern" code={extraction.from_filename_pattern} />
          )}
          {extraction.from_path_prefix && Object.keys(extraction.from_path_prefix).length > 0 && (
            <DetailRow label="path_prefix">
              {Object.entries(extraction.from_path_prefix).map(([k, v]) => (
                <Tag key={k} className="text-xs">
                  {k} → {v}
                </Tag>
              ))}
            </DetailRow>
          )}
          {extraction.default_value != null && (
            <DetailRow
              label="default"
              code={
                Array.isArray(extraction.default_value) ? extraction.default_value.join(", ") : extraction.default_value
              }
            />
          )}
          {extraction.from_llm && (
            <DetailRow label="from_llm" code={extraction.from_llm.output_type} hint={extraction.from_llm.prompt_hint} />
          )}
          {extraction.from_field && <DetailRow label="from_field" code={extraction.from_field} />}
          {extraction.transform && <DetailRow label="transform" code={extraction.transform} />}
          {extraction.value_map && Object.keys(extraction.value_map).length > 0 && (
            <DetailRow label="value_map">
              {Object.entries(extraction.value_map).map(([k, v]) => (
                <Tag key={k} className="text-xs">
                  {k} → {v}
                </Tag>
              ))}
            </DetailRow>
          )}
          {extraction.regex_flags && extraction.regex_flags.length > 0 && (
            <DetailRow label="regex_flags">{extraction.regex_flags.join(", ")}</DetailRow>
          )}
        </div>
      )}

      {vectorstore && (vectorstore.prompt_template || vectorstore.embedding_template) && (
        <div className="mt-2 p-2 bg-noa-inset rounded border border-noa text-xs space-y-1">
          {vectorstore.prompt_template && <DetailRow label="prompt_template" code={vectorstore.prompt_template} />}
          {vectorstore.embedding_template && (
            <DetailRow label="embedding_template" code={vectorstore.embedding_template} />
          )}
        </div>
      )}
    </div>
  );
}
